import React from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LogOut, ClipboardList, Images, Settings2, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="animate-pulse text-zinc-400 uppercase tracking-widest text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const linkBase =
    "flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest transition-colors border-l-2";
  const linkClass = ({ isActive }) =>
    `${linkBase} ${
      isActive
        ? "text-cyan-400 bg-cyan-400/5 border-cyan-400"
        : "text-zinc-400 border-transparent hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-72 md:min-h-screen border-b md:border-b-0 md:border-r border-white/10 bg-black/60 backdrop-blur-md">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <Logo size="sm" />
        </div>
        <nav className="py-4">
          <NavLink to="/admin/quotes" data-testid={ADMIN.sidebarQuotes} className={linkClass}>
            <ClipboardList className="w-4 h-4" />
            Quotes
          </NavLink>
          <NavLink to="/admin/gallery" data-testid={ADMIN.sidebarGallery} className={linkClass}>
            <Images className="w-4 h-4" />
            Gallery
          </NavLink>
          <NavLink to="/admin/settings" data-testid={ADMIN.sidebarSettings} className={linkClass}>
            <Settings2 className="w-4 h-4" />
            Settings
          </NavLink>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`${linkBase} text-zinc-400 border-transparent hover:text-white hover:bg-white/5`}
          >
            <ExternalLink className="w-4 h-4" />
            View site
          </a>
        </nav>
        <div className="p-4 mt-auto border-t border-white/10">
          <Button
            onClick={logout}
            data-testid={ADMIN.logout}
            variant="outline"
            className="rounded-none w-full border-white/10 bg-transparent hover:bg-red-500/10 hover:border-red-400/50 hover:text-red-400 text-zinc-300 uppercase tracking-widest text-xs"
          >
            <LogOut className="mr-2 w-4 h-4" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 max-w-full overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
